import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import loginIMG from '../../../assets/loginIMG2.png';
import { authService, captchaService, sha512Hex, encryptPasswordForLogin } from '../../../services/authService';
import { loginFormError, LOGIN_ID_MAX_LENGTH, CAPTCHA_LENGTH } from '../../../shared/validation/authInput';

interface LoginPageProps {
  onLogin?: () => void;
}

function LoginPage({ onLogin }: LoginPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Seconds left before the captcha may be requested again (429 rate limit)
  const [cooldown, setCooldown] = useState(0);
  /**
   * Seconds left on an account-level block — either the 15-minute lockout after
   * 5 failed attempts (423) or the short progressive delay between attempts
   * (429 LOGIN_THROTTLED). Both come with the server's own wait time, and both
   * disable the form: submitting again before it expires is always rejected.
   */
  const [lockedFor, setLockedFor] = useState(0);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  // Load captcha on component mount
  useEffect(() => {
    loadCaptcha();
  }, []);

  // Tick the rate-limit cooldown down; reload the captcha automatically when it ends.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => {
      const next = cooldown - 1;
      setCooldown(next);
      if (next === 0) loadCaptcha();
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  /**
   * A failed login reloads the captcha, because the one just submitted is spent.
   * Pass `clearError: false` on that path — the replacement arrives a few
   * hundred milliseconds later, and clearing the banner then would erase the
   * message explaining why the login failed before the user has read it.
   */
  // Tick the account block down; clear the banner once it expires so the user
  // can try again without reloading the page.
  useEffect(() => {
    if (lockedFor <= 0) return;
    const timer = setTimeout(() => {
      const next = lockedFor - 1;
      setLockedFor(next);
      if (next === 0) setError('');
    }, 1000);
    return () => clearTimeout(timer);
  }, [lockedFor]);

  /** "14:59" for a long lockout, "8s" for a short throttle. */
  const formatWait = (seconds: number): string =>
    seconds >= 60
      ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
      : `${seconds}s`;

  const loadCaptcha = async (clearError = true) => {
    try {
      const captchaData = await captchaService.createCaptcha();
      setCaptchaId(captchaData.captchaId);
      setCaptchaSvg(captchaData.captchaSvg);
      if (clearError) setError('');
      setCooldown(0);
    } catch (err: any) {
      console.error('Failed to load captcha:', err);

      // Rate limited (429). Telling the user to refresh would make it worse —
      // every retry counts against the same limit. Show the wait time instead
      // and reload automatically when it expires.
      if (err?.status === 429) {
        const wait = Math.max(1, Math.ceil(err.retryAfter ?? 60));
        setCooldown(wait);
        if (clearError) setError('');
        return;
      }

      if (err?.status === 0) {
        setError('Cannot reach the server. Please check your internet connection.');
        return;
      }

      setError(err?.message || 'Could not load the captcha. Please try again in a moment.');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Security audit (Improper Input Validation): malformed input is refused
    // here with a reason instead of being sent. The server applies the same
    // rules regardless.
    const inputError = loginFormError(loginId, captcha);
    if (inputError) {
      setError(inputError);
      return;
    }

    setLoading(true);

    try {
      // Security audit #2: the password never leaves the browser readable.
      // `passwordHash` (SHA-512) is the credential for accounts on the current
      // scheme; `passwordEnc` is the same password RSA-encrypted to the server's
      // public key, which is the only thing that can verify — and then migrate —
      // an account still stored under the old bcrypt(raw) scheme.
      const [passwordHash, passwordEnc] = await Promise.all([
        sha512Hex(password),
        encryptPasswordForLogin(password)
      ]);

      const response = await authService.login({
        username: loginId,
        passwordHash,
        ...(passwordEnc ? { passwordEnc } : {}),
        captchaId: captchaId,
        captchaValue: captcha
      });

      if (response.success) {
        // Success - navigate to the page they tried to visit, or dashboard
        const from = (location.state as any)?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
        if (onLogin) onLogin();
      } else {
        setError(response.error || 'Login failed');
        // Reload captcha on error, keeping the message that was just set
        loadCaptcha(false);
        setCaptcha('');
        // Security audit: credentials must not survive a failed attempt — a
        // populated password field is readable by anyone who reaches the
        // unattended screen and is restorable from the DOM.
        setPassword('');
      }
    } catch (err: any) {
      setCaptcha('');
      setPassword('');

      /**
       * Account-level blocks, both carrying the server's own wait time:
       *   423 ACCOUNT_LOCKED   — 5 failures inside 10 minutes → 15-minute lock
       *   429 LOGIN_THROTTLED  — progressive delay between attempts (2s, 4s, 8s…)
       * Checked before the plain 429 branch below, which is the per-IP limiter
       * and means something different ("this network", not "this account").
       */
      if (err?.status === 423 || err?.code === 'LOGIN_THROTTLED') {
        setError(err?.message || 'Too many failed login attempts. Please try again later.');
        setLockedFor(Math.max(1, Math.ceil(err?.retryAfter ?? 60)));
        loadCaptcha(false);
        return;
      }

      // Login rate limit (429) — show the wait and hold off on requesting a new
      // captcha, which would be rejected by the limiter anyway.
      if (err?.status === 429) {
        const wait = Math.max(1, Math.ceil(err.retryAfter ?? 60));
        setError(err.message || 'Too many login attempts. Please wait before trying again.');
        setCooldown(wait);
        return;
      }

      // The server distinguishes the two 401s: USER_NOT_FOUND names the Login ID
      // (and says it is case-sensitive), INVALID_CREDENTIALS covers a wrong
      // password. Showing its message verbatim keeps the two in one place —
      // appending a capitalisation hint here would wrongly blame the Login ID
      // when the ID was right and only the password was wrong.
      setError(err?.message || 'An error occurred during login');
      // Reload captcha on error — the used one is now invalid. Keep the message.
      loadCaptcha(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshCaptcha = () => {
    if (cooldown > 0) return; // still rate limited — a request now would just fail
    loadCaptcha();
    setCaptcha('');
  };

  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Set by ForgotPasswordPage / ChangePasswordPage after a successful update.
  const passwordJustReset = Boolean((location.state as any)?.passwordReset);

  // Land straight on the form when arriving from a password reset.
  useEffect(() => {
    if (passwordJustReset) setShowForm(true);
  }, [passwordJustReset]);

  return (
    <div className="w-full min-h-full flex items-center justify-center px-4 py-10">
  <div className="relative rounded-2xl shadow-2xl overflow-hidden w-full max-w-[1000px] lg:h-[487px] bg-white">
        {/* Left side: Image. The artwork carries its own S-curve and its own
            white right-hand third. Absolute, at natural size, so the form's
            offset is an explicit number — the old marginRight:-200px slid it
            over the heading. */}
        <img
          src={loginIMG}
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute left-0 top-0 h-full w-[630px] object-cover pointer-events-none select-none"
        />
        {/* Right side: Login card. lg:pl clears the curve's widest point
            (x=627, around y=48-72 — exactly where the heading sits). */}
        <div className="relative flex flex-col items-center justify-center py-8 px-4 sm:px-10 lg:pl-[646px] lg:pr-12 lg:h-full" style={{ minWidth: '0', minHeight: '480px' }}>
          {!showForm ? (
            <div className="flex flex-col items-center gap-2 w-full max-w-sm">
              {/* User Icon */}
              <svg width="60" height="60" fill="none" stroke="#6d28d9" strokeWidth="1" viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
              </svg>
              {/* Title */}
              <div className="text-lg md:text-xl font-medium text-purple-800 text-center">Vidya Samiksha Kendra</div>
              {/* Login Button */}
              <button
                className="mt-2 px-8 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold text-base md:text-lg shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors"
                onClick={() => setShowForm(true)}
              >
                Login
              </button>
            </div>
          ) : (
            // autoComplete="off" — security audit finding #11: disable browser autofill/credential saving
            <form onSubmit={handleSubmit} autoComplete="off" className="w-full max-w-sm flex flex-col gap-0 p-0 m-0">
              <div className="flex flex-col items-center mb-4">
                <svg width="40" height="40" fill="none" stroke="#6d28d9" strokeWidth="1" viewBox="0 0 24 24" className="lg:w-12 lg:h-12">
                  <circle cx="12" cy="8" r="6" />
                  <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                </svg>
                <div className="text-base lg:text-lg font-semibold text-purple-700">Login</div>
              </div>
              {/* Error message. Deliberately compact: the card is a fixed height
                  on lg, so a tall banner pushes the fields down and the layout
                  jumps between attempts. */}
              {error && (
                <div
                  role="alert"
                  className="bg-red-50 border-l-2 border-red-400 text-red-700 px-3 py-1.5 rounded-r text-xs leading-snug mb-2"
                >
                  {error}
                  {lockedFor > 0 && (
                    <span className="block mt-0.5 font-semibold tabular-nums">
                      You can try again in {formatWait(lockedFor)}.
                    </span>
                  )}
                </div>
              )}
              {/* Rate limited (429) — show the wait, not "please refresh" */}
              {cooldown > 0 && (
                <div className="bg-amber-50 border-l-2 border-amber-400 text-amber-800 px-3 py-1.5 rounded-r text-xs leading-snug mb-2">
                  Too many requests from your network. Please wait{' '}
                  <span className="font-semibold">{cooldown}s</span> — the captcha will load automatically.
                </div>
              )}
              {/* Confirmation after a password reset or change */}
              {passwordJustReset && !error && (
                <div className="bg-green-50 border-l-2 border-green-400 text-green-700 px-3 py-1.5 rounded-r text-xs leading-snug mb-2">
                  Password updated. Please log in with your new password.
                </div>
              )}
              {/* Login ID */}
              <div>
                <label htmlFor="loginId" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Login ID <span className="font-normal text-gray-500">(case-sensitive)</span>
                </label>
                <input
                  type="text"
                  id="loginId"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  placeholder="Enter Login ID"
                  required
                  maxLength={LOGIN_ID_MAX_LENGTH}
                  autoComplete="off"
                  // Login IDs are case-sensitive on the server. Without these a
                  // mobile keyboard capitalises the first letter and the login
                  // fails with no visible reason.
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={loading}
                  className="w-full border-b border-gray-300 focus:border-purple-600 outline-none py-2 px-1 mb-1 text-sm md:text-base bg-transparent disabled:bg-gray-100"
                  onPaste={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                />
              </div>
              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>

                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter Password"
                    required
                    autoComplete="new-password"
                    disabled={loading}
                    className="w-full border-b border-gray-300 focus:border-purple-600 outline-none py-2 px-1 pr-10 mb-1 text-sm md:text-base bg-transparent disabled:bg-gray-100"
                    onPaste={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                 />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    disabled={loading}
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800 disabled:cursor-not-allowed"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      // Eye-off icon
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.58 10.58A2 2 0 0013.42 13.42M9.88 5.09A9.77 9.77 0 0112 5c5 0 9 5 9 7a11.4 11.4 0 01-2.08 3.11M6.1 6.1C3.91 7.6 2.4 9.7 2 12c0 2 4 7 10 7a9.74 9.74 0 004.32-.99" />
                      </svg>
                    ) : (
                      // Eye icon
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {/* Captcha */}
              <div>
                <label htmlFor="captcha" className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  Captcha
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    id="captcha"
                    value={captcha}
                    onChange={(e) => setCaptcha(e.target.value)}
                    placeholder="Enter Captcha"
                    required
                    maxLength={CAPTCHA_LENGTH}
                    autoComplete="off"
                    disabled={loading}
                    className="flex-1 min-w-0 border-b border-gray-300 focus:border-purple-600 outline-none py-1 px-1 mb-1 text-sm md:text-base bg-transparent disabled:bg-gray-100"
                  onPaste={(e) => e.preventDefault()}
  onCopy={(e) => e.preventDefault()}
  onCut={(e) => e.preventDefault()}
                 
                 />
                  {/* Captcha SVG Display */}
                  {captchaSvg ? (
                    <div
                      className="h-12 w-32 border border-gray-300 rounded flex items-center justify-center bg-white"
                      dangerouslySetInnerHTML={{ __html: captchaSvg }}
                    />
                  ) : (
                    <div className="h-10 w-24 border border-gray-300 rounded flex items-center justify-center bg-gray-100">
                      <span className="text-xs text-gray-400">
                        {cooldown > 0 ? `Wait ${cooldown}s` : 'Loading...'}
                      </span>
                    </div>
                  )}
                  {/* Refresh Button — disabled during a rate-limit cooldown so
                      retries cannot dig the limit deeper */}
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    disabled={loading || cooldown > 0}
                    className="h-10 w-10 border border-gray-300 rounded flex items-center justify-center hover:bg-gray-50 transition-colors disabled:cursor-not-allowed"
                    title="Refresh Captcha"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Forgot password */}
              <div className="mt-2">
                <Link
                  to="/forgot-password"
                  className="text-xs text-purple-700 hover:text-purple-900 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              {/* Login Button */}
              {/* Blocked while the account lockout or throttle is counting down —
                  the server rejects the attempt anyway, and a disabled button
                  makes the wait obvious instead of looking like a failure. */}
              <button
                type="submit"
                disabled={loading || lockedFor > 0}
                className="w-full lg:w-[120px] rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold text-base lg:text-lg shadow-md hover:from-purple-700 hover:to-purple-800 transition-colors mt-4 lg:mt-6 disabled:bg-purple-400 disabled:cursor-not-allowed lg:ml-auto"
                style={{ height: '50px' }}
              >
                {loading ? 'Logging in...' : lockedFor > 0 ? formatWait(lockedFor) : 'Login'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
